import React, { useCallback, useEffect, useState } from 'react'
import {
  Card,
  Form,
  InputNumber,
  Button,
  Typography,
  Spin,
  message,
  Space,
  Result,
  Radio,
  Select,
  Switch,
  Divider
} from 'antd'
import {
  ReloadOutlined,
  SaveOutlined,
  ShoppingOutlined,
  CalculatorOutlined,
  PercentageOutlined
} from '@ant-design/icons'
import styled from 'styled-components'
import { useAuth } from '../hooks/AuthContext'
import { useMembership } from '../hooks/useMembership'
import { useNavigate } from 'react-router-dom'
import api from '../utils/api'
import Hint from '../components/common/Hint'

const { Title, Paragraph, Text } = Typography

const PageWrapper = styled.div`
  min-height: calc(100vh - 120px);
  padding: 32px;
  background: #f5f5f5;

  @media (max-width: 768px) {
    padding: 16px;
  }
`

const ContentWrapper = styled.div`
  max-width: 780px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const StyledCard = styled(Card)`
  border-radius: 18px;
  box-shadow: 0 18px 40px rgba(213, 114, 138, 0.12);
  border: 1px solid rgba(213, 114, 138, 0.08);

  .ant-card-head {
    border-bottom: none;
    padding-bottom: 0;
  }
`

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  span {
    color: #8c8c8c;
    font-size: 14px;
  }
`

const FormWrapper = styled(Form)`
  margin-top: 16px;

  .ant-form-item {
    margin-bottom: 20px;
  }
`

const RateInputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  background: #fff5f7;
  border: 1px solid #f4d6de;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
`

const LabelText = styled.span`
  color: #576079;
  font-weight: 600;
  font-size: 16px;
  min-width: 120px;
`

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;

  @media (max-width: 480px) {
    flex-direction: column;

    button {
      width: 100%;
    }
  }
`

const InfoCard = styled(Card)`
  border-radius: 16px;
  background: #ffffffcc;
  border: 1px dashed #f0ccd4;
`

// 規則卡片風格 Radio：使用 Radio.Group 包覆兩張卡片
const RuleRadioGroup = styled(Radio.Group)`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;

  .ant-radio-wrapper {
    margin: 0;
    padding: 16px;
    border: 1px solid #eef0f5;
    border-radius: 12px;
    background: #fff;
    transition: all 0.18s ease;
    align-items: flex-start;
    width: 100%;
  }

  .ant-radio-wrapper:hover {
    border-color: #d5728a;
    background: #fffafb;
  }

  .ant-radio-wrapper-checked {
    border-color: #d5728a;
    background: #fff5f7;
    box-shadow: 0 4px 12px rgba(213, 114, 138, 0.12);
  }

  .ant-radio {
    margin-top: 2px;
  }
`

const RuleLabel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-left: 4px;

  .rule-name {
    font-size: 15px;
    font-weight: 600;
    color: #1c1c1e;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .rule-formula {
    font-size: 13px;
    color: #6b7280;
    font-family: ui-monospace, SFMono-Regular, monospace;
    background: #f4f5f7;
    padding: 6px 10px;
    border-radius: 6px;
    align-self: flex-start;
  }
  .rule-desc {
    font-size: 12px;
    color: #8e8e93;
    line-height: 1.5;
  }
`

const ProductSalesCommissionSettingsPage = () => {
  const { user, businessPlan } = useAuth()
  const navigate = useNavigate()
  const { hasFeature, permissionsLoaded } = useMembership()
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [employees, setEmployees] = useState([])
  const [storeProducts, setStoreProducts] = useState([])
  const [teamBonus, setTeamBonus] = useState({
    enabled: false,
    items: [],
    memberIds: []
  })

  const businessId = user?.businessId

  const fetchOptions = useCallback(async () => {
    if (!businessId) return
    try {
      const [empRes, prodRes] = await Promise.all([
        api.get(`/api/employees/${businessId}`),
        api.get('/api/store/products', { params: { page: 1, pageSize: 500 } })
      ])
      setEmployees(Array.isArray(empRes.data) ? empRes.data : [])
      setStoreProducts(prodRes.data?.data?.items || [])
    } catch (error) {
      console.error('取得員工或商品清單失敗', error)
    }
  }, [businessId])

  const fetchCommissionSettings = useCallback(async () => {
    if (!businessId) return
    try {
      setLoading(true)
      const response = await api.get(
        `/api/businesses/${businessId}/product-sales/commission`
      )
      const payload =
        response.data?.data ||
        response.data?.commission ||
        response.data ||
        null

      const commissionRate =
        payload?.commissionRate !== undefined
          ? Number(payload.commissionRate)
          : payload?.rate !== undefined
          ? Number(payload.rate)
          : 0
      const commissionRule =
        payload?.commissionRule === 'profit_rate_minus_fee'
          ? 'profit_rate_minus_fee'
          : 'sales_rate'

      form.setFieldsValue({
        commissionRule,
        commissionRate
      })
      const tb = payload?.teamBonus || {}
      setTeamBonus({
        enabled: !!tb.enabled,
        items: Array.isArray(tb.items) ? tb.items : [],
        memberIds: Array.isArray(tb.memberIds) ? tb.memberIds.map(String) : []
      })
    } catch (error) {
      console.error('取得產品銷售抽成設定失敗', error)
      form.setFieldsValue({
        commissionRule: 'sales_rate',
        commissionRate: 0
      })
    } finally {
      setLoading(false)
    }
  }, [businessId, form])

  useEffect(() => {
    if (!hasFeature('salary')) return
    fetchCommissionSettings()
    fetchOptions()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId, hasFeature])

  // 權限檢查：只有品牌方案才能使用此功能
  if (!permissionsLoaded) {
    return (
      <PageWrapper>
        <div style={{ textAlign: 'center', padding: '100px' }}>
          <Spin size='large' tip='正在載入權限...' />
        </div>
      </PageWrapper>
    )
  }

  if (!hasFeature('salary')) {
    return (
      <PageWrapper>
        <ContentWrapper>
          <Result
            status='403'
            title='功能限制'
            subTitle='此功能需要品牌方案才能使用，請升級您的會員方案。'
            extra={
              <Button
                type='primary'
                onClick={() => navigate('/settings/membership')}
              >
                查看會員方案
              </Button>
            }
          />
        </ContentWrapper>
      </PageWrapper>
    )
  }

  const handleReset = () => {
    form.resetFields()
    form.setFieldsValue({
      commissionRule: 'sales_rate',
      commissionRate: 0
    })
  }

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()
      if (!businessId) {
        message.error('未能取得商家資訊，請重新登入後再試')
        return
      }

      setSaving(true)
      await api.put(`/api/businesses/${businessId}/product-sales/commission`, {
        commissionRule:
          values.commissionRule === 'profit_rate_minus_fee'
            ? 'profit_rate_minus_fee'
            : 'sales_rate',
        commissionRate: Number(values.commissionRate) || 0,
        teamBonus: {
          enabled: !!teamBonus.enabled,
          items: teamBonus.items
            .filter((i) => i.productId || i.productName)
            .map((i) => ({
              productId: i.productId || undefined,
              productName: i.productName || '',
              amountPerUnit: Number(i.amountPerUnit) || 0
            })),
          memberIds: teamBonus.memberIds
        }
      })
      message.success('產品銷售抽成設定已更新')
    } catch (error) {
      if (error?.errorFields) {
        return
      }
      console.error('更新產品銷售抽成設定失敗', error)
      message.error(
        error.response?.data?.message ||
          error.message ||
          '更新產品銷售抽成設定失敗，請稍後再試'
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <PageWrapper>
      <ContentWrapper>
        <StyledCard
          title={
            <Header>
              <Title level={3} style={{ marginBottom: 0, color: '#0f2562' }}>
                產品銷售抽成設定
              </Title>
              <span>
                設定產品銷售的固定抽成比例，用於計算員工的產品銷售抽成。
              </span>
            </Header>
          }
        >
          <Paragraph style={{ color: '#576079', marginBottom: 24 }}>
            設定產品銷售的抽成比例（0-100%）。此比例將應用於所有產品銷售金額的抽成計算。
            <br />
            <Text type='secondary' style={{ fontSize: '13px' }}>
              ⚠️ 重要：如果沒有設定，產品銷售抽成為 0
            </Text>
          </Paragraph>

          <Spin spinning={loading}>
            <FormWrapper
              layout='vertical'
              form={form}
              initialValues={{
                commissionRule: 'sales_rate',
                commissionRate: 0
              }}
            >
              <Form.Item
                name='commissionRule'
                label={
                  <span style={{ fontSize: 15, fontWeight: 600, color: '#1c1c1e' }}>
                    計算規則
                  </span>
                }
                style={{ marginBottom: 24 }}
              >
                <RuleRadioGroup>
                  <Radio value='sales_rate'>
                    <RuleLabel>
                      <span className='rule-name'>
                        <PercentageOutlined style={{ color: '#3498db' }} />
                        按業績抽成（標準規則）
                      </span>
                      <span className='rule-formula'>
                        抽成 = 商品業績 × 抽成%
                      </span>
                      <span className='rule-desc'>
                        最常見的規則。按商品銷售金額直接乘以抽成比例。
                        不需要設定商品成本，計算單純。
                      </span>
                    </RuleLabel>
                  </Radio>
                  <Radio value='profit_rate_minus_fee'>
                    <RuleLabel>
                      <span className='rule-name'>
                        <CalculatorOutlined style={{ color: '#d5728a' }} />
                        按真實利潤抽成扣手續費（含折扣、成本與金流費）
                      </span>
                      <span className='rule-formula'>
                        抽成 =（商品實收 − 成本）× 抽成% − 該商品手續費
                      </span>
                      <span className='rule-desc'>
                        • <b>商品實收</b>：售價自動扣除折扣、優惠券、會員折扣的分攤。例：原價 200、整單 8 折 → 商品實收 160。
                        <br />
                        • <b>成本</b>：自動帶入庫存模組裡該商品的成本欄位（需先在庫存管理填寫）。
                        <br />
                        • <b>手續費</b>：當付款含刷卡 / 行動支付時，按商品實收占訂單實收的比例分攤；現金、轉帳則為 0。
                        <br />
                        • 若實收低於成本（過度折扣），利潤歸 0，抽成歸 0（員工不會倒貼）。
                      </span>
                    </RuleLabel>
                  </Radio>
                </RuleRadioGroup>
              </Form.Item>

              <Hint
                title='提示'
                tone='info'
                style={{ marginBottom: 16, borderRadius: 10 }}
              >
                選擇「按利潤抽成」前，請先到「庫存管理」設定每個商品的成本價格，否則成本會以 0 計算（等同售價全為利潤）。
              </Hint>

              <RateInputRow>
                <LabelText>抽成比例</LabelText>
                <Form.Item
                  name='commissionRate'
                  rules={[
                    { required: true, message: '請輸入抽成比例' },
                    {
                      type: 'number',
                      min: 0,
                      max: 100,
                      message: '抽成比例必須在 0-100% 之間'
                    }
                  ]}
                  style={{ marginBottom: 0, flex: 1 }}
                >
                  <InputNumber
                    min={0}
                    max={100}
                    placeholder='輸入抽成比例'
                    style={{ width: '100%', maxWidth: 300 }}
                    addonAfter='%'
                    formatter={(value) =>
                      value === undefined || value === null ? '' : `${value}`
                    }
                    parser={(value) =>
                      value ? Number(value.replace(/[^\d.]/g, '')) : 0
                    }
                  />
                </Form.Item>
              </RateInputRow>

              <Divider />
              <div style={{ fontSize: 15, fontWeight: 600, color: '#1c1c1e', marginBottom: 8 }}>
                團積獎金（指定商品，成員均分）
              </div>
              <Paragraph type='secondary' style={{ fontSize: 13 }}>
                指定商品不論誰賣出，全店當月售出件數 × 每件獎金，由下方成員平均分配。
                這些商品不再另外依上方比例抽成（業績仍照算給銷售人員）。
              </Paragraph>
              <Space style={{ marginBottom: 12 }}>
                <Switch
                  checked={teamBonus.enabled}
                  onChange={(v) => setTeamBonus((p) => ({ ...p, enabled: v }))}
                  checkedChildren='已啟用'
                  unCheckedChildren='未啟用'
                />
              </Space>
              <Space direction='vertical' style={{ width: '100%' }}>
                {teamBonus.items.map((it, idx) => (
                  <Space key={idx} wrap>
                    <Select
                      showSearch
                      optionFilterProp='label'
                      style={{ width: 240 }}
                      placeholder='選擇商品'
                      value={it.productId || undefined}
                      onChange={(v) => {
                        const prod = storeProducts.find((p) => String(p._id) === v)
                        setTeamBonus((p) => ({
                          ...p,
                          items: p.items.map((x, i) =>
                            i === idx
                              ? { ...x, productId: v, productName: prod?.name || prod?.title || '' }
                              : x
                          )
                        }))
                      }}
                      options={storeProducts.map((p) => ({
                        value: String(p._id),
                        label: p.name || p.title
                      }))}
                    />
                    <InputNumber
                      min={0}
                      value={it.amountPerUnit}
                      prefix='NT$'
                      addonAfter='/ 件'
                      style={{ width: 180 }}
                      onChange={(v) =>
                        setTeamBonus((p) => ({
                          ...p,
                          items: p.items.map((x, i) =>
                            i === idx ? { ...x, amountPerUnit: v || 0 } : x
                          )
                        }))
                      }
                    />
                    <Button
                      danger
                      type='link'
                      onClick={() =>
                        setTeamBonus((p) => ({
                          ...p,
                          items: p.items.filter((_, i) => i !== idx)
                        }))
                      }
                    >
                      移除
                    </Button>
                  </Space>
                ))}
                <Button
                  type='dashed'
                  onClick={() =>
                    setTeamBonus((p) => ({
                      ...p,
                      items: [...p.items, { productId: undefined, productName: '', amountPerUnit: 0 }]
                    }))
                  }
                >
                  ＋ 新增商品
                </Button>
                <div style={{ marginTop: 8 }}>
                  <Text>均分成員</Text>
                  <Select
                    mode='multiple'
                    style={{ width: '100%', maxWidth: 480, display: 'block', marginTop: 6 }}
                    placeholder='選擇要平分獎金的人員'
                    value={teamBonus.memberIds}
                    onChange={(v) => setTeamBonus((p) => ({ ...p, memberIds: v }))}
                    options={employees.map((e) => ({
                      value: String(e._id),
                      label: e.name || e.username || '未命名員工'
                    }))}
                  />
                </div>
              </Space>

              <Actions>
                <Button icon={<ReloadOutlined />} onClick={handleReset}>
                  清除
                </Button>
                <Button
                  type='primary'
                  icon={<SaveOutlined />}
                  onClick={handleSubmit}
                  loading={saving}
                  disabled={loading}
                >
                  保存設定
                </Button>
              </Actions>
            </FormWrapper>
          </Spin>
        </StyledCard>

        <InfoCard>
          <Title level={5} style={{ color: '#0f2562', marginBottom: 12 }}>
            設定說明
          </Title>
          <Paragraph style={{ marginBottom: 6 }}>
            <Text strong>• 規則一（按業績抽成）：</Text>產品抽成 = 產品銷售金額 × 抽成比例（%）
          </Paragraph>
          <Paragraph style={{ marginBottom: 6 }}>
            <Text strong>• 規則二（按真實利潤扣手續費）：</Text>
            產品抽成 =（商品實收 − 成本）× 抽成% − 該商品分攤之金流手續費；最低為 0
          </Paragraph>
          <Paragraph style={{ marginBottom: 6 }}>
            <Text strong>• 商品實收</Text>
            自動分攤所有折扣、優惠券、會員折扣後的金額（例：8 折時實收 = 售價 × 0.8）
          </Paragraph>
          <Paragraph style={{ marginBottom: 6 }}>
            <Text strong>• 成本</Text>
            自動讀取「庫存管理」中該商品的成本欄位
          </Paragraph>
          <Paragraph style={{ marginBottom: 6 }}>
            <Text strong>• 手續費</Text>
            僅刷卡 / 行動支付會列入；現金、轉帳免扣
          </Paragraph>
          <Paragraph style={{ marginBottom: 6 }}>
            <Text strong>• 適用範圍</Text>
            此設定為店家層級，套用到所有員工的產品銷售抽成
          </Paragraph>
          <Paragraph>
            <Text strong>• 若沒有設定抽成比例，產品銷售抽成為 0</Text>
          </Paragraph>
        </InfoCard>
      </ContentWrapper>
    </PageWrapper>
  )
}

export default ProductSalesCommissionSettingsPage
